import React, { useState, useEffect } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const GOLD = '#D4AF37';

const AVAILABLE_CODES = [
  'ASHRAE 90.1', 'ASHRAE 62.1', 'CA Title 24', 'IBC', 'IMC',
  'IPC', 'IECC', 'NFPA 70 / NEC', 'SMACNA',
];

const SPEC_FORMATS = ['CSI MasterFormat', 'CSI UniFormat', 'Custom'];

const TABS = ['Company Info', 'Technical Standards', 'Manufacturers', 'Review Templates'];

const empty = () => ({
  companyName: '', address: '', city: '', state: '',
  licenseNumber: '', contactName: '', contactEmail: '', contactPhone: '',
  defaultCodes: [], seismicZone: '', climateZone: '',
  preferredSpecFormat: 'CSI MasterFormat',
  preferredManufacturers: [], excludedManufacturers: [],
  submittalStampLanguage: {
    approved: 'APPROVED',
    approvedAsNoted: 'APPROVED AS NOTED',
    reviseResubmit: 'REVISE AND RESUBMIT',
    rejected: 'REJECTED',
  },
  reportDisclaimer: 'This review is for general conformance with the design intent and contract documents only.',
  rfiResponseDisclaimer: '',
  signatureBlock: '',
});

const styles = {
  container: {
    maxWidth: 820,
    margin: '0 auto',
    padding: '32px 24px',
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subheading: {
    fontSize: 13,
    color: '#666',
    marginBottom: 24,
  },
  tabs: {
    display: 'flex',
    gap: 4,
    borderBottom: '2px solid #e5e7eb',
    marginBottom: 28,
  },
  tab: (active) => ({
    padding: '9px 18px',
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: active ? GOLD : '#555',
    background: 'none',
    border: 'none',
    borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
    marginBottom: -2,
    cursor: 'pointer',
    fontFamily: 'sans-serif',
  }),
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '24px 28px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: GOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 16,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px 20px',
    marginBottom: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#444',
  },
  input: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
    outline: 'none',
  },
  textarea: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
    outline: 'none',
    resize: 'vertical',
    minHeight: 72,
  },
  select: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
    background: '#fff',
  },
  checkGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '8px 12px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 13,
    color: '#1a1a1a',
    cursor: 'pointer',
  },
  addRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  addInput: {
    flex: 1,
    padding: '7px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
  },
  addBtn: {
    padding: '7px 14px',
    background: GOLD,
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'sans-serif',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    fontSize: 12,
    color: '#1a1a1a',
    margin: '3px 4px 3px 0',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
  },
  saveBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginTop: 8,
  },
  saveBtn: {
    padding: '10px 28px',
    background: GOLD,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'sans-serif',
  },
  savedMsg: {
    fontSize: 13,
    color: '#16a34a',
    fontWeight: 600,
  },
  errorMsg: {
    fontSize: 13,
    color: '#dc2626',
  },
};

function Field({ label, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

export default function FirmProfileSettings({ user }) {
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Add-row state for manufacturer lists
  const [newPref, setNewPref] = useState({ name: '', category: '' });
  const [newExcl, setNewExcl] = useState({ name: '', reason: '' });

  const userId = user?.email || '';

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.profile) {
          setForm(f => ({ ...f, ...data.profile }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const setStamp = (key, value) =>
    setForm(f => ({ ...f, submittalStampLanguage: { ...f.submittalStampLanguage, [key]: value } }));

  const toggleCode = (code) =>
    setForm(f => ({
      ...f,
      defaultCodes: f.defaultCodes.includes(code)
        ? f.defaultCodes.filter(c => c !== code)
        : [...f.defaultCodes, code],
    }));

  const addPref = () => {
    if (!newPref.name.trim()) return;
    setForm(f => ({ ...f, preferredManufacturers: [...f.preferredManufacturers, { ...newPref }] }));
    setNewPref({ name: '', category: '' });
  };

  const removePref = (i) =>
    setForm(f => ({ ...f, preferredManufacturers: f.preferredManufacturers.filter((_, j) => j !== i) }));

  const addExcl = () => {
    if (!newExcl.name.trim()) return;
    setForm(f => ({ ...f, excludedManufacturers: [...f.excludedManufacturers, { ...newExcl }] }));
    setNewExcl({ name: '', reason: '' });
  };

  const removeExcl = (i) =>
    setForm(f => ({ ...f, excludedManufacturers: f.excludedManufacturers.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Save failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, color: '#666', fontFamily: 'sans-serif' }}>Loading profile…</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.heading}>Company Intelligence Profile</div>
      <div style={styles.subheading}>
        These settings personalize every report, letter, and analysis ZT AI Tools generates for your firm.
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map((t, i) => (
          <button key={t} style={styles.tab(activeTab === i)} onClick={() => setActiveTab(i)}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab 1: Company Info */}
      {activeTab === 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Company Info</div>
          <div style={styles.row}>
            <Field label="Company Name">
              <input style={styles.input} value={form.companyName} onChange={e => set('companyName', e.target.value)} />
            </Field>
            <Field label="License Number">
              <input style={styles.input} value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} />
            </Field>
          </div>
          <div style={{ ...styles.row, gridTemplateColumns: '2fr 1fr 1fr' }}>
            <Field label="Address">
              <input style={styles.input} value={form.address} onChange={e => set('address', e.target.value)} />
            </Field>
            <Field label="City">
              <input style={styles.input} value={form.city} onChange={e => set('city', e.target.value)} />
            </Field>
            <Field label="State">
              <input style={styles.input} value={form.state} onChange={e => set('state', e.target.value)} />
            </Field>
          </div>
          <div style={styles.row}>
            <Field label="Primary Contact Name">
              <input style={styles.input} value={form.contactName} onChange={e => set('contactName', e.target.value)} />
            </Field>
            <Field label="Contact Email">
              <input style={styles.input} type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
            </Field>
          </div>
          <div style={{ ...styles.row, gridTemplateColumns: '1fr 2fr' }}>
            <Field label="Contact Phone">
              <input style={styles.input} value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} />
            </Field>
          </div>
        </div>
      )}

      {/* Tab 2: Technical Standards */}
      {activeTab === 1 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Technical Standards</div>
          <Field label="Default Codes & Standards">
            <div style={{ ...styles.checkGrid, marginTop: 6 }}>
              {AVAILABLE_CODES.map(code => (
                <label key={code} style={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.defaultCodes.includes(code)}
                    onChange={() => toggleCode(code)}
                    style={{ accentColor: GOLD }}
                  />
                  {code}
                </label>
              ))}
            </div>
          </Field>
          <div style={{ ...styles.row, marginTop: 18 }}>
            <Field label="Seismic Zone">
              <input style={styles.input} placeholder="e.g. SDC D" value={form.seismicZone} onChange={e => set('seismicZone', e.target.value)} />
            </Field>
            <Field label="Climate Zone">
              <input style={styles.input} placeholder="e.g. Zone 6" value={form.climateZone} onChange={e => set('climateZone', e.target.value)} />
            </Field>
          </div>
          <div style={{ ...styles.row, gridTemplateColumns: '1fr 2fr', marginTop: 0 }}>
            <Field label="Preferred Spec Format">
              <select style={styles.select} value={form.preferredSpecFormat} onChange={e => set('preferredSpecFormat', e.target.value)}>
                {SPEC_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>
        </div>
      )}

      {/* Tab 3: Manufacturers */}
      {activeTab === 2 && (
        <>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Preferred Manufacturers</div>
            <div style={styles.addRow}>
              <input
                style={styles.addInput}
                placeholder="Manufacturer name"
                value={newPref.name}
                onChange={e => setNewPref(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addPref()}
              />
              <input
                style={styles.addInput}
                placeholder="Category (e.g. HVAC)"
                value={newPref.category}
                onChange={e => setNewPref(p => ({ ...p, category: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addPref()}
              />
              <button style={styles.addBtn} onClick={addPref}>+ Add</button>
            </div>
            <div>
              {form.preferredManufacturers.length === 0 && (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No preferred manufacturers added yet.</div>
              )}
              {form.preferredManufacturers.map((m, i) => (
                <span key={i} style={styles.tag}>
                  <strong>{m.name}</strong>{m.category ? ` — ${m.category}` : ''}
                  <button style={styles.removeBtn} onClick={() => removePref(i)}>✕</button>
                </span>
              ))}
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Excluded Manufacturers</div>
            <div style={styles.addRow}>
              <input
                style={styles.addInput}
                placeholder="Manufacturer name"
                value={newExcl.name}
                onChange={e => setNewExcl(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addExcl()}
              />
              <input
                style={styles.addInput}
                placeholder="Reason (e.g. Not approved)"
                value={newExcl.reason}
                onChange={e => setNewExcl(p => ({ ...p, reason: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addExcl()}
              />
              <button style={styles.addBtn} onClick={addExcl}>+ Add</button>
            </div>
            <div>
              {form.excludedManufacturers.length === 0 && (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No excluded manufacturers added yet.</div>
              )}
              {form.excludedManufacturers.map((m, i) => (
                <span key={i} style={styles.tag}>
                  <strong>{m.name}</strong>{m.reason ? ` — ${m.reason}` : ''}
                  <button style={styles.removeBtn} onClick={() => removeExcl(i)}>✕</button>
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Tab 4: Review Templates */}
      {activeTab === 3 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Submittal Stamp Language</div>
          <div style={styles.row}>
            <Field label="Approved">
              <input style={styles.input} value={form.submittalStampLanguage.approved} onChange={e => setStamp('approved', e.target.value)} />
            </Field>
            <Field label="Approved as Noted">
              <input style={styles.input} value={form.submittalStampLanguage.approvedAsNoted} onChange={e => setStamp('approvedAsNoted', e.target.value)} />
            </Field>
            <Field label="Revise & Resubmit">
              <input style={styles.input} value={form.submittalStampLanguage.reviseResubmit} onChange={e => setStamp('reviseResubmit', e.target.value)} />
            </Field>
            <Field label="Rejected">
              <input style={styles.input} value={form.submittalStampLanguage.rejected} onChange={e => setStamp('rejected', e.target.value)} />
            </Field>
          </div>

          <div style={styles.sectionTitle}>Disclaimers & Signature</div>
          <Field label="Report Disclaimer">
            <textarea
              style={styles.textarea}
              value={form.reportDisclaimer}
              onChange={e => set('reportDisclaimer', e.target.value)}
            />
          </Field>
          <div style={{ marginTop: 14 }}>
            <Field label="RFI Response Disclaimer">
              <textarea
                style={styles.textarea}
                value={form.rfiResponseDisclaimer}
                onChange={e => set('rfiResponseDisclaimer', e.target.value)}
              />
            </Field>
          </div>
          <div style={{ marginTop: 14 }}>
            <Field label="Signature Block">
              <textarea
                style={styles.textarea}
                placeholder={'e.g.\nJohn Smith, PE\nMechanical Engineer of Record\nLicense #M12345'}
                value={form.signatureBlock}
                onChange={e => set('signatureBlock', e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}

      {/* Save bar */}
      <div style={styles.saveBar}>
        <button style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
        {saved && <span style={styles.savedMsg}>✓ Profile saved</span>}
        {error && <span style={styles.errorMsg}>{error}</span>}
      </div>
    </div>
  );
}
